import ReactEChartsCore from 'echarts-for-react/lib/core';
import * as echarts from 'echarts/core';
import { TooltipComponent, GridComponent, LegendComponent } from 'echarts/components';
import { BarChart } from 'echarts/charts';
import { CanvasRenderer } from 'echarts/renderers';
import { useAppContext } from '../../../../providers/AppProvider';
import { Card } from 'react-bootstrap';
import React from 'react';

echarts.use([TooltipComponent, GridComponent, LegendComponent, BarChart, CanvasRenderer]);

interface PromptInjectionRiskFactorsChartProps {
  averageFAISS: number;
  averageClassifier: number;
  averageObfuscation: number;
  averageToxicity: number;
  overallPIRS: number;
  eventsAnalyzed: number;
  title: string;
  description: string;
}

const PromptInjectionRiskFactorsChart: React.FC<PromptInjectionRiskFactorsChartProps> = ({
  averageFAISS,
  averageClassifier,
  averageObfuscation,
  averageToxicity,
  overallPIRS,
  eventsAnalyzed,
  title,
  description
}) => {
  const { getThemeColor } = useAppContext();

  const riskFactors = [
    { name: 'FAISS Sim', value: averageFAISS },
    { name: 'Classifier Prob', value: averageClassifier },
    { name: 'Obfuscation', value: averageObfuscation },
    { name: 'Toxicity', value: averageToxicity },
    { name: 'PIRS Score', value: overallPIRS }
  ];

  // Ensure we have valid colors with fallbacks
  const dangerColor = getThemeColor('danger') || '#dc3545';
  const warningColor = getThemeColor('warning') || '#ffc107';
  const infoColor = getThemeColor('info') || '#0dcaf0';
  const bodyHighlightBg = getThemeColor('body-highlight-bg') || '#ffffff';
  const borderColor = getThemeColor('border-color') || '#dee2e6';
  const secondaryColor = getThemeColor('secondary-color') || '#6c757d';
  const lightTextEmphasis = getThemeColor('light-text-emphasis') || '#495057';

  const factorNames = riskFactors.map(factor => factor.name);
  const factorValues = riskFactors.map(factor => factor.value);

  // Determine color based on value (higher = more risk)
  const getBarColor = (value: number) => {
    if (value >= 0.7) return dangerColor;
    if (value >= 0.35) return warningColor;
    if (value > 0) return infoColor;
    return borderColor;
  };

  const options = {
    tooltip: {
      trigger: 'axis',
      backgroundColor: bodyHighlightBg,
      borderColor: borderColor,
      textStyle: { color: lightTextEmphasis },
      borderWidth: 1,
      formatter: (params: any) => {
        const param = params[0];
        return `<strong>${param.name}:</strong> ${param.value.toFixed(
          4
        )}<br/><strong>Events Analyzed:</strong> ${eventsAnalyzed}`;
      }
    },
    xAxis: {
      type: 'category',
      name: 'Risk Factor',
      data: factorNames,
      axisLine: { lineStyle: { color: borderColor } },
      axisLabel: {
        color: secondaryColor,
        rotate: 45
      },
      nameTextStyle: { color: secondaryColor }
    },
    yAxis: {
      type: 'value',
      name: 'Average Value (0-1)',
      min: 0,
      max: 1,
      axisLine: { lineStyle: { color: borderColor } },
      axisLabel: { color: secondaryColor },
      nameTextStyle: { color: secondaryColor },
      splitLine: {
        show: true,
        lineStyle: {
          color: borderColor,
          type: 'dashed'
        }
      }
    },
    series: [
      {
        type: 'bar',
        data: factorValues.map(value => ({
          value,
          itemStyle: {
            color: getBarColor(value),
            borderRadius: [5, 5, 0, 0]
          }
        })),
        barWidth: '50%',
        label: {
          show: true,
          position: 'top',
          formatter: (params: any) => {
            return params.value > 0 ? params.value.toFixed(4) : '';
          },
          color: lightTextEmphasis,
          fontSize: 11
        }
      }
    ],
    grid: { left: '10%', right: '10%', bottom: '20%', top: '15%' }
  };

  return (
    <Card className="h-100">
      <Card.Body>
        <div className="d-flex justify-content-between">
          <div>
            <h4 className="mb-1">{title}</h4>
            <h6 className="text-body-tertiary">{description}</h6>
          </div>
        </div>
        <div className="d-flex justify-content-center px-4 py-6">
          <ReactEChartsCore echarts={echarts} option={options} style={{ height: '400px', width: '100%' }} />
        </div>
      </Card.Body>
    </Card>
  );
};

export default PromptInjectionRiskFactorsChart;
